import { uploadAnalyzedDocuments, getOrCreateDraftSessionId } from '@/lib/upload-deal-documents';
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, X, FileText, Loader2, Sparkles, AlertCircle, CheckCircle } from 'lucide-react';
import { postApi } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';

// ÉLARGI (28 août 2026) — schéma étendu pour capturer immobilier
// structuré, inventaire détaillé, licences, culture/transition (incluant
// le rayon de non-concurrence), SWOT, et impact/main-d'œuvre — en plus
// des champs financiers déjà extraits. Voir ai.service.ts
// analyzeFinancialDocuments() pour le schéma JSON complet correspondant.
interface AnalyzedData {
  legalName?: string | null;
  tradeName?: string | null;
  entityType?: string | null;
  yearEstablished?: number | null;
  jurisdiction?: string | null;
  naicsCode?: string | null;
  website?: string | null;
  reasonForSale?: string | null;
  accountsReceivable?: number | null;
  grossRevenue?: { year1?: number | null; year2?: number | null; year3?: number | null }
  ebitda?: { year1?: number | null; year2?: number | null; year3?: number | null };
  sde?: number | null;
  cashFlow?: number | null;
  totalAssets?: number | null;
  totalLiabilities?: number | null;
  inheritedDebt?: number | null;
  inventory?: number | null;
  ffe?: number | null;
  realEstate?: number | null;
  askingPriceSuggested?: number | null;
  employees?: number | null;
  partTimeEmployees?: number | null;
  ownerSalary?: number | null;
  ownerHoursPerWeek?: number | null;
  customerConcentration?: number | null;
  keySuppliers?: string | null;
  addbacks?: { description: string; amount: number }[];
  projections?: {
    year1Revenue?: number | null; year2Revenue?: number | null; year3Revenue?: number | null;
    year1Ebitda?: number | null; year2Ebitda?: number | null; year3Ebitda?: number | null;
  };
  realEstateProperties?: {
    address: string;
    estimatedValue?: number | null;
    status: 'owned' | 'leased';
    monthlyRent?: number | null;
    leaseExpirationDate?: string | null;
    remainingLeaseMonths?: number | null;
  }[];
  inventoryItems?: { description: string; category?: string | null; quantity?: number | null; unitValue?: number | null }[];
  licensing?: {
    specialLicensesRequired?: boolean | null;
    licenseDetails?: string | null;
    regulatoryRequirements?: string | null;
    legalDisputes?: string | null;
  };
  culture?: {
    coreValues?: string[];
    managementTeamInPlace?: boolean | null;
    managementTeamExplanation?: string | null;
    transitionDurationMonths?: number | null;
    nonCompetePeriodYears?: number | null;
    nonCompeteRadius?: number | null;
    nonCompeteRadiusUnit?: 'km' | 'miles' | null;
    sellerFinancingOffered?: boolean | null;
    sellerFinancingAmount?: number | null;
    sellerFinancingInterestRate?: number | null;
    sellerFinancingTermMonths?: number | null;
  };
  swot?: {
    strengths?: string | null;
    weaknesses?: string | null;
    opportunities?: string | null;
    threats?: string | null;
  };
  impact?: {
    employeeCount?: number | null;
    averageEmployeeTenureYears?: number | null;
    hasRetirementPlan?: boolean | null;
    workplaceAccidentsLast3yr?: number | null;
    localSupplyChainPercent?: number | null;
    hasEnvironmentalCertification?: boolean | null;
    hasLitigationHistory?: boolean | null;
    communityInvolvement?: boolean | null;
  };
  confidence?: Record<string, string>;
  missingFields?: string[];
  notes?: string;
}

interface Props {
  onDataExtracted: (data: AnalyzedData, uploadedDocs: { name: string; mimeType: string; url: string; path: string }[]) => void;
  onSkip: () => void;
}

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_FILES = 6;
const MAX_SIZE_MB = 10;

export const FinancialDocumentAnalyzer = ({ onDataExtracted, onSkip }: Props) => {
  const { language } = useLanguage();
  const [files, setFiles] = useState<{ name: string; base64: string; mimeType: string; size: number }[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalyzedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
const [uploading, setUploading] = useState(false);
  const fr = language === 'fr';

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    const valid = picked.filter(f => ACCEPTED_TYPES.includes(f.type) && f.size <= MAX_SIZE_MB * 1024 * 1024);
    const newFiles = await Promise.all(
      valid.slice(0, MAX_FILES - files.length).map(async f => ({
        name: f.name,
        base64: await toBase64(f),
        mimeType: f.type,
        size: f.size,
      }))
    );
    setFiles(prev => [...prev, ...newFiles]);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setResult(null);
  };

  const analyze = async () => {
    if (files.length === 0) return;
    setAnalyzing(true);
    setError(null);
    try {
      const data = await postApi('/ai/analyze-documents', { documents: files });
      setResult(data);
    } catch (err: any) {
      setError(err?.message || (fr ? 'Erreur lors de l\'analyse' : 'Analysis error'));
    } finally {
      setAnalyzing(false);
    }
  };

 const applyAndContinue = async () => {
  if (!result) return;
  setUploading(true);
  try {
    const sessionId = getOrCreateDraftSessionId();
    const uploadedDocs = await uploadAnalyzedDocuments(sessionId, files);
    onDataExtracted(result, uploadedDocs);
  } catch (err: any) {
    setError(err?.message || (fr ? 'Erreur upload documents' : 'Document upload error'));
  } finally {
    setUploading(false);
  }
};

  const formatCurrency = (val?: number | null) =>
    val ? `$${val.toLocaleString('en-US')}` : '—';

  const getConfidenceColor = (level?: string) => {
    if (level === 'high') return 'text-green-600';
    if (level === 'medium') return 'text-yellow-600';
    return 'text-red-500';
  };

  // AJOUTÉ (28 août 2026) — Option A : résumé condensé (un compteur par
  // catégorie) plutôt qu'un détail complet de chaque propriété/item/SWOT
  // extrait. L'utilisateur révise le détail réel à l'étape correspondante
  // du formulaire (5, 4, 3, 9, 10) — cet aperçu sert seulement à confirmer
  // que l'extraction a bien trouvé quelque chose dans chaque catégorie.
  const extendedSummaryItems: { label: string; count: number }[] = result ? [
    { label: fr ? 'Propriété(s) immobilière(s)' : 'Real estate propertie(s)', count: result.realEstateProperties?.length || 0 },
    { label: fr ? "Item(s) d'inventaire" : 'Inventory item(s)', count: result.inventoryItems?.length || 0 },
    { label: fr ? 'Valeur(s) fondamentale(s)' : 'Core value(s)', count: result.culture?.coreValues?.length || 0 },
    { label: fr ? 'Licence(s)/conformité détectée(s)' : 'License(s)/compliance detected', count: result.licensing?.licenseDetails ? 1 : 0 },
    { label: fr ? 'Analyse SWOT détectée' : 'SWOT analysis detected', count: (result.swot?.strengths || result.swot?.weaknesses || result.swot?.opportunities || result.swot?.threats) ? 1 : 0 },
    { label: fr ? "Données d'impact/main-d'œuvre" : 'Impact/workforce data', count: result.impact?.employeeCount ? 1 : 0 },
  ].filter(item => item.count > 0) : [];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-[#CEAB65]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-[#CEAB65]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1D314D]">
          {fr ? 'Préremplissage intelligent' : 'Smart Auto-Fill'}
        </h2>
        <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
          {fr
            ? 'Uploadez vos documents financiers et corporatifs. Claude analysera et préremplira automatiquement votre formulaire.'
            : 'Upload your financial and corporate documents. Claude will automatically analyze and pre-fill your form.'}
        </p>
      </div>

      {/* Upload Zone */}
      <Card className="border-2 border-dashed border-[#CEAB65]/40 hover:border-[#CEAB65] transition-colors cursor-pointer"
        onClick={() => inputRef.current?.click()}>
        <CardContent className="py-8 text-center">
          <Upload className="w-10 h-10 text-[#CEAB65] mx-auto mb-3" />
          <p className="font-medium text-[#1D314D]">
            {fr ? 'Cliquez pour ajouter des documents' : 'Click to add documents'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {fr ? 'PDF, JPG, PNG — max 10MB par fichier — max 6 fichiers'
              : 'PDF, JPG, PNG — max 10MB per file — max 6 files'}
          </p>
          <div className="flex flex-wrap gap-2 justify-center mt-3">
            {['P&L', 'Balance Sheet', fr ? 'Décl. fiscale (T2)' : 'Tax Return (T2)',
              fr ? 'Docs corporatifs' : 'Corporate Docs',
              fr ? 'Contrats majeurs' : 'Major Contracts',
              fr ? 'Bail / titre de propriété' : 'Lease / property deed'].map(label => (
              <span key={label} className="bg-[#1D314D]/10 text-[#1D314D] text-xs px-2 py-1 rounded-full">
                {label}
              </span>
            ))}
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={handleFilePick}
          />
        </CardContent>
      </Card>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#CEAB65]" />
                <span className="text-sm font-medium truncate max-w-[250px]">{f.name}</span>
                <span className="text-xs text-muted-foreground">
                  ({(f.size / 1024 / 1024).toFixed(1)} MB)
                </span>
              </div>
              <button onClick={(e) => { e.stopPropagation(); removeFile(i); }}>
                <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Result Preview */}
      {result && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              {fr ? 'Données extraites' : 'Extracted Data'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {result.legalName && (
                <div><span className="text-muted-foreground">{fr ? 'Nom légal:' : 'Legal name:'}</span> <strong>{result.legalName}</strong></div>
              )}
              {result.yearEstablished && (
                <div><span className="text-muted-foreground">{fr ? 'Fondée:' : 'Founded:'}</span> <strong>{result.yearEstablished}</strong></div>
              )}
              {result.grossRevenue?.year1 && (
                <div><span className="text-muted-foreground">{fr ? 'Revenus (an 1):' : 'Revenue (yr 1):'}</span> <strong className={getConfidenceColor(result.confidence?.grossRevenue)}>{formatCurrency(result.grossRevenue.year1)}</strong></div>
              )}
              {result.ebitda?.year1 && (
                <div><span className="text-muted-foreground">EBITDA (an 1):</span> <strong className={getConfidenceColor(result.confidence?.ebitda)}>{formatCurrency(result.ebitda.year1)}</strong></div>
              )}
              {result.sde && (
                <div><span className="text-muted-foreground">SDE:</span> <strong className={getConfidenceColor(result.confidence?.sde)}>{formatCurrency(result.sde)}</strong></div>
              )}
              {result.inventory && (
                <div><span className="text-muted-foreground">{fr ? 'Inventaire:' : 'Inventory:'}</span> <strong>{formatCurrency(result.inventory)}</strong></div>
              )}
              {result.askingPriceSuggested && (
                <div><span className="text-muted-foreground">{fr ? 'Prix suggéré:' : 'Suggested price:'}</span> <strong className="text-[#CEAB65]">{formatCurrency(result.askingPriceSuggested)}</strong></div>
              )}
            </div>

            {/* AJOUTÉ — résumé condensé des catégories élargies détectées
                (Option A : compteurs seulement, détail révisé à l'étape
                correspondante du formulaire). */}
            {extendedSummaryItems.length > 0 && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="text-xs font-medium text-green-800 mb-1.5">
                  {fr ? 'Également détecté :' : 'Also detected:'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {extendedSummaryItems.map((item) => (
                    <span key={item.label} className="bg-white text-green-800 text-xs px-2 py-1 rounded-full border border-green-200">
                      {item.count > 1 ? `${item.count} ${item.label}` : item.label}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  {fr
                    ? 'Vous pourrez réviser ces détails aux étapes correspondantes du formulaire.'
                    : 'You will be able to review these details at the corresponding form steps.'}
                </p>
              </div>
            )}

            {result.missingFields && result.missingFields.length > 0 && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="text-xs text-muted-foreground">
                  {fr ? '⚠️ Champs non trouvés (à compléter manuellement):' : '⚠️ Missing fields (complete manually):'}
                  {' '}{result.missingFields.join(', ')}
                </p>
              </div>
            )}
            {result.notes && (
              <p className="text-xs text-muted-foreground mt-2 italic">{result.notes}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-between pt-2">
        <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
          {fr ? 'Passer cette étape →' : 'Skip this step →'}
        </Button>
        <div className="flex gap-3">
          {files.length > 0 && !result && (
            <Button
              onClick={analyze}
              disabled={analyzing}
              className="bg-[#1D314D] text-white hover:bg-[#2E4663]"
            >
              {analyzing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{fr ? 'Analyse en cours...' : 'Analyzing...'}</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />{fr ? 'Analyser avec Claude' : 'Analyze with Claude'}</>
              )}
            </Button>
          )}
          {result && (
  <Button
    onClick={applyAndContinue}
    disabled={uploading}
    className="bg-[#CEAB65] text-[#1D314D] hover:bg-[#b8954f] font-bold"
  >
    {uploading ? (
      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{fr ? 'Sauvegarde...' : 'Saving...'}</>
    ) : (
      <><CheckCircle className="w-4 h-4 mr-2" />{fr ? 'Appliquer et continuer →' : 'Apply & Continue →'}</>
    )}
  </Button>
          )}
        </div>
      </div>
    </div>
  );
};